import { trackGoogleAnalyticsEvent, trackGoogleAnalyticsPageView } from "./googleAnalytics.js";
import { trackPixelAnalyticsEvent, trackPixelPageView } from "./pixelAnalytics.js";

const QUEUE_KEY = "mall_event_sdk_queue";
const DEVICE_ID_KEY = "mall_event_sdk_device_id";
const SESSION_KEY = "mall_event_sdk_session";
const FIRST_TOUCH_KEY = "mall_event_sdk_first_touch";
const LAST_ROUTE_KEY = "mall_event_sdk_last_route";

const SESSION_TTL = 30 * 60 * 1000;
const MAX_QUEUE_SIZE = 500;
const MAX_BATCH_SIZE = 20;
const MAX_QUEUE_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const BASE_RETRY_MS = 5 * 1000;
const MAX_RETRY_MS = 5 * 60 * 1000;
const UPLOAD_TIMEOUT_MS = 15 * 1000;
const REQUEST_FAILURE_DEDUP_WINDOW_MS = 15 * 1000;
const API_REQUEST_FAIL_EVENT_CODE = "api_request_fail";
const REQUEST_FAILURE_IGNORE_PATTERNS = ["/mallEventLog/batchSave"];

const EVENT_CODE_MAP = {
  app_init: "app_init",
  click_home_register_coupon_banner: "click_home_register_coupon_banner",
  auth_login_attempt: "auth_login_attempt",
  auth_login_success: "auth_login_success",
  auth_login_fail: "auth_login_fail",
  auth_register_attempt: "auth_register_attempt",
  auth_register_success: "auth_register_success",
  auth_register_fail: "auth_register_fail",
  tb_goods_detail_view: "product_detail_view",
  tb_add_to_cart: "add_cart_success",
  tb_buy_now: "buy_now_click",
  tb_cart_checkout: "checkout_click",
  tb_cart_delete: "cart_delete",
  tb_cart_select_item: "cart_select_item",
  tb_cart_select_all: "cart_select_all",
  tb_cart_select_store: "cart_select_store",
  alibaba_goods_detail_view: "product_detail_view",
  alibaba_add_to_cart: "add_cart_success",
  alibaba_buy_now: "buy_now_click",
  alibaba_cart_checkout: "checkout_click",
  alibaba_cart_delete: "cart_delete",
  alibaba_cart_select_item: "cart_select_item",
  alibaba_cart_select_all: "cart_select_all",
  alibaba_cart_select_store: "cart_select_store",
  tb_order_create_start: "create_order_submit",
  tb_order_create_success: "create_order_success",
  tb_order_create_fail: "create_order_fail",
  tb_order_create_error: "create_order_fail",
  tb_payment_start: "pay_submit",
  tb_payment_success: "pay_success",
  tb_payment_fail: "pay_fail",
  tb_payment_error: "pay_fail",
  alibaba_order_create_start: "create_order_submit",
  alibaba_order_create_success: "create_order_success",
  alibaba_order_create_fail: "create_order_fail",
  alibaba_payment_start: "pay_submit",
  alibaba_payment_success: "pay_success",
  alibaba_payment_fail: "pay_fail",
  alibaba_payment_error: "pay_fail",
  expert_purchase_success: "pay_success",
};

const ROUTE_PAGE_CODE_MAP = {
  "/": "home",
  "/mall/home": "home",
  "/mall/cart": "cart_list",
  "/tb/goods": "product_detail",
  "/wd/goods": "product_detail",
  "/mall/tbDirectPaymentConfirm": "direct_payment_confirm",
  "/mall/tbPayOrder": "order_confirm",
  "/mall/platformAllOrderListV2": "order_list",
  "/mall/orderList": "order_list",
  "/shop/expertPurchaseHome": "expert_purchase",
};

let initialized = false;
let queueCache = null;
let flushTimer = null;
let flushing = false;
let online = true;
let networkType = "unknown";
let contextProvider = null;
const requestFailureDedupMap = new Map();

function now() {
  return Date.now();
}

function getWindow() {
  return typeof window !== "undefined" ? window : null;
}

function getStorage() {
  return getWindow()?.localStorage || null;
}

function randomId(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

function formatDate(date) {
  const pad = n => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function safeJsonParse(value, fallback) {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function safeJsonStringify(value) {
  try {
    return JSON.stringify(value);
  } catch {
    return "{}";
  }
}

function getByPath(source, path) {
  if (!source || !path) return undefined;
  return String(path).split(".").reduce((current, key) => {
    if (current === undefined || current === null) return undefined;
    return current[key];
  }, source);
}

function toBoolean(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  return false;
}

function normalizeMallApiBaseUrl(rawUrl = "") {
  let nextUrl = String(rawUrl || "").trim();
  if (!nextUrl) return "";
  nextUrl = nextUrl.replace(/\/+$/, "").replace(/\/mall$/i, "");
  const win = getWindow();
  if (win) {
    if (win.location.protocol === "http:" && nextUrl.startsWith("https://")) {
      nextUrl = nextUrl.replace("https://", "http://");
    } else if (win.location.protocol === "https:" && nextUrl.startsWith("http://")) {
      nextUrl = nextUrl.replace("http://", "https://");
    }
  }
  return nextUrl.replace(/\/+$/, "");
}

function parseOtherJson(storeInfo = {}) {
  const parse = value => {
    if (!value) return {};
    if (typeof value === "object") return value;
    return safeJsonParse(value, {});
  };
  const offiaccountFollowData = parse(storeInfo.offiaccountFollowData || storeInfo.offiaccountFollowUrl);
  const candidates = [
    storeInfo?.offiaccountFollowData?.otherJson,
    storeInfo?.storePageInfo?.otherJson,
    storeInfo?.otherJson,
    offiaccountFollowData?.otherJson,
    storeInfo?.storePageInfo,
  ];
  for (const candidate of candidates) {
    const parsed = parse(candidate);
    if (parsed && typeof parsed === "object" && Object.keys(parsed).length) {
      return parsed;
    }
  }
  return {};
}

function getStoredStoreInformation() {
  const storage = getStorage();
  if (!storage) return {};
  const storeInfo = safeJsonParse(storage.getItem("StoreInformation"), {});
  if (typeof storeInfo?.offiaccountFollowUrl === "string" && !storeInfo.offiaccountFollowData) {
    storeInfo.offiaccountFollowData = safeJsonParse(storeInfo.offiaccountFollowUrl, {});
  }
  if (typeof storeInfo?.offiaccountFollowData?.otherJson === "string") {
    storeInfo.offiaccountFollowData.otherJson = safeJsonParse(storeInfo.offiaccountFollowData.otherJson, {});
  }
  return storeInfo || {};
}

function getContext() {
  if (typeof contextProvider === "function") {
    try {
      return contextProvider() || {};
    } catch {
      return {};
    }
  }
  return {};
}

export function setMallEventLogContextProvider(provider) {
  contextProvider = typeof provider === "function" ? provider : null;
}

function getStoreInfo() {
  const context = getContext();
  const explicit = context.storeInfo || context.storeInfoData;
  if (explicit && typeof explicit === "object" && Object.keys(explicit).length) {
    return explicit;
  }
  return getStoredStoreInformation();
}

function getSupportEventLog(app) {
  const candidates = [
    getByPath(app, "shopInfor.offiaccountFollowData.otherJson.supportEventLog"),
    getByPath(app, "storeInfoData.offiaccountFollowData.otherJson.supportEventLog"),
    getByPath(getStoreInfo(), "offiaccountFollowData.otherJson.supportEventLog"),
    getByPath(getStoreInfo(), "storePageInfo.otherJson.supportEventLog"),
    getByPath(getStoreInfo(), "otherJson.supportEventLog"),
    parseOtherJson(getStoreInfo()).supportEventLog,
  ];
  return candidates.some(toBoolean);
}

function getApiUrl() {
  const context = getContext();
  const mallApiBaseUrl = normalizeMallApiBaseUrl(
    context.mallApiUrl || parseOtherJson(getStoreInfo()).mallApiUrl || getStoreInfo().mallApiUrl,
  );
  if (mallApiBaseUrl) {
    return `${mallApiBaseUrl}/mall/admin/mallEventLog/batchSave`;
  }
  return "/api/mall/admin/mallEventLog/batchSave";
}

function getAuthToken() {
  const storage = getStorage();
  const context = getContext();
  return (
    context.authToken ||
    context.mallToken ||
    storage?.getItem("mall_app_user_key") ||
    storage?.getItem("sj_mall_admin_token") ||
    storage?.getItem("token") ||
    storage?.getItem("authorization") ||
    ""
  );
}

function getDeviceId() {
  const storage = getStorage();
  if (!storage) return randomId("d");
  const cached = storage.getItem(DEVICE_ID_KEY);
  if (cached) return cached;
  const value = randomId("d");
  storage.setItem(DEVICE_ID_KEY, value);
  return value;
}

function getSessionId() {
  const storage = getStorage();
  if (!storage) return randomId("s");
  const cached = safeJsonParse(storage.getItem(SESSION_KEY), {});
  const isExpired = !cached.id || !cached.updatedAt || now() - cached.updatedAt > SESSION_TTL;
  const next = {
    id: isExpired ? randomId("s") : cached.id,
    updatedAt: now(),
  };
  storage.setItem(SESSION_KEY, JSON.stringify(next));
  return next.id || randomId("s");
}

function parseUrlParams() {
  const win = getWindow();
  if (!win) return {};
  const full = `${win.location.search || ""}&${(win.location.hash || "").split("?")[1] || ""}`;
  const params = new URLSearchParams(full.replace(/^&/, ""));
  const result = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}

function getFirstTouch() {
  const storage = getStorage();
  if (!storage) return {};
  const cached = safeJsonParse(storage.getItem(FIRST_TOUCH_KEY), {});
  if (Object.keys(cached).length) return cached;
  const params = parseUrlParams();
  const touch = {
    channel: params.channel || params.utm_source || params.source || params.scene || params.shareCode || params.inviteCode || "",
    utm_source: params.utm_source || "",
    utm_campaign: params.utm_campaign || "",
    source: params.source || "",
    scene: params.scene || "",
  };
  storage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));
  return touch;
}

function getUserId(app) {
  const context = getContext();
  const candidates = [
    context.userId,
    getByPath(app, "mallUserInfo.id"),
    getByPath(context, "mallUserInfo.id"),
    getByPath(context, "userInfo.mallUserInfo.id"),
    getByPath(context, "userInfo.id"),
    getByPath(context, "user.id"),
  ];
  const found = candidates.find(value => value !== null && value !== undefined && value !== "");
  return found || null;
}

function pickValue(source = {}, keys = []) {
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
}

function toNullableNumber(value) {
  if (value === undefined || value === null || value === "") return null;
  const normalized = Array.isArray(value) ? value[0] : value;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function toNullableString(value) {
  if (value === undefined || value === null || value === "") return null;
  if (Array.isArray(value)) return value.length ? String(value[0]) : null;
  return String(value);
}

function resolveOrderSource(source = {}, screenName = "") {
  const explicit = toNullableNumber(pickValue(source, ["orderSource", "order_source", "sourcePlatform"]));
  if (explicit) return explicit;
  const platform = String(pickValue(source, ["platform", "platformType", "platform_type", "origin"]) || "").toLowerCase();
  if (platform.includes("1688") || platform.includes("alibaba") || platform === "2") return 2;
  if (platform.includes("tb") || platform.includes("taobao") || platform === "1") return 1;
  if (String(screenName).toLowerCase().includes("alibaba")) return 2;
  return 1;
}

function resolvePageCode(screenName = "", source = {}, fallback = "") {
  const explicit = toNullableString(pickValue(source, ["pageCode", "page_code", "screen_name", "screenName"]));
  if (explicit) return explicit;
  const path = String(screenName || getWindow()?.location?.pathname || "");
  const matchedKey = Object.keys(ROUTE_PAGE_CODE_MAP)
    .sort((a, b) => b.length - a.length)
    .find(key => path === key || path.startsWith(`${key}/`));
  return matchedKey ? ROUTE_PAGE_CODE_MAP[matchedKey] : (fallback || screenName || "unknown_page");
}

function getCurrentChannel(source = {}) {
  const explicit = toNullableString(pickValue(source, ["channel", "utm_source", "source", "scene"]));
  if (explicit) return explicit;
  const touch = getFirstTouch();
  return touch.channel || touch.utm_source || touch.source || touch.scene || "direct";
}

function getWebNetworkType() {
  const win = getWindow();
  if (!win) return "unknown";
  const connection = win.navigator?.connection || win.navigator?.mozConnection || win.navigator?.webkitConnection;
  return connection?.effectiveType || connection?.type || (win.navigator?.onLine === false ? "offline" : "online");
}

function buildExtJson(base = {}, source = {}) {
  return safeJsonStringify({
    ...getFirstTouch(),
    networkType,
    originalEventName: base.originalEventName,
    screenName: base.screenName,
    routePath: base.routePath,
    fromRouteName: base.fromRouteName,
    fromRoutePath: base.fromRoutePath,
    source,
  });
}

function buildPayload(eventCode, source = {}, meta = {}) {
  const screenName = toNullableString(meta.screenName) || undefined;
  const productSpuId = toNullableNumber(pickValue(source, ["productSpuId", "product_spu_id", "itemId", "item_id"]));
  const productSkuId = toNullableNumber(pickValue(source, ["productSkuId", "product_sku_id", "itemSkuId", "item_sku_id", "skuId", "sku_id"]));
  const rawOrderId =
    pickValue(source, ["orderId", "order_id", "purchaseId", "purchase_id"]) ||
    pickValue(source, ["orderIds", "order_ids"]) ||
    pickValue(source, ["purchase_ids", "purchaseIds"]);
  const orderId = Array.isArray(rawOrderId)
    ? rawOrderId[0]
    : typeof rawOrderId === "string" && rawOrderId.includes(",")
      ? rawOrderId.split(",")[0]
      : rawOrderId;

  return {
    eventCode,
    eventTime: formatDate(new Date()),
    userId: getUserId(meta.app),
    deviceId: getDeviceId(),
    sessionId: getSessionId(),
    terminal: "web",
    channel: getCurrentChannel(source),
    pageCode: resolvePageCode(screenName, source, meta.pageCode),
    productSpuId,
    productSkuId,
    orderId: toNullableString(orderId),
    orderSource: resolveOrderSource(source, screenName),
    extJson: buildExtJson(meta, source),
  };
}

function pruneQueue(queue) {
  const expireBefore = now() - MAX_QUEUE_AGE_MS;
  return queue.filter(item => item.createdAt >= expireBefore);
}

function readQueue() {
  if (queueCache) return queueCache;
  queueCache = pruneQueue(safeJsonParse(getStorage()?.getItem(QUEUE_KEY), []));
  return queueCache;
}

function writeQueue(queue) {
  queueCache = pruneQueue(queue).slice(-MAX_QUEUE_SIZE);
  getStorage()?.setItem(QUEUE_KEY, JSON.stringify(queueCache));
}

function scheduleFlush(delay = 2000) {
  if (!getWindow()) return;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushMallEventLogQueue().catch(error => {
      if (process.env.NODE_ENV === "development") {
        console.warn("[mall-event-log] flush failed", error);
      }
    });
  }, delay);
}

function enqueue(payload) {
  const queue = readQueue();
  queue.push({
    id: randomId("evt"),
    payload,
    retryCount: 0,
    nextRetryAt: now(),
    createdAt: now(),
  });
  writeQueue(queue);
  scheduleFlush(800);
}

function bumpRetry(queue, ids) {
  const next = queue.map((item) => {
    if (!ids.includes(item.id)) return item;
    const retryCount = item.retryCount + 1;
    const retryDelay = Math.min(BASE_RETRY_MS * Math.pow(2, retryCount - 1), MAX_RETRY_MS);
    return {
      ...item,
      retryCount,
      nextRetryAt: now() + retryDelay,
    };
  });
  writeQueue(next);
  scheduleFlush(BASE_RETRY_MS);
}

async function sendBatch(payloads, keepalive = false) {
  const headers = {
    "Content-Type": "application/json",
  };
  const token = getAuthToken();
  if (token) headers.authorization = token;

  const controller = typeof AbortController !== "undefined" ? new AbortController() : undefined;
  const timer = controller ? setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS) : null;
  try {
    const response = await fetch(getApiUrl(), {
      method: "POST",
      headers,
      body: JSON.stringify(payloads),
      keepalive,
      signal: controller?.signal,
    });
    if (!response.ok) {
      throw new Error(`mall event upload failed: ${response.status}`);
    }
    const data = await response.json().catch(() => ({ success: true }));
    if (data && data.success === false) {
      throw new Error(data.msg || "mall event upload failed");
    }
    return data;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function flushMallEventLogQueue(options = {}) {
  if (!getSupportEventLog() || flushing || !online) return;
  flushing = true;
  try {
    const queue = readQueue();
    const readyItems = queue.filter(item => item.nextRetryAt <= now()).slice(0, MAX_BATCH_SIZE);
    if (!readyItems.length) return;
    await sendBatch(readyItems.map(item => item.payload), !!options.keepalive);
    const sentIds = readyItems.map(item => item.id);
    writeQueue(queue.filter(item => !sentIds.includes(item.id)));
    if (readQueue().some(item => item.nextRetryAt <= now())) {
      scheduleFlush(500);
    }
  } catch (error) {
    bumpRetry(readQueue(), readQueue().filter(item => item.nextRetryAt <= now()).slice(0, MAX_BATCH_SIZE).map(item => item.id));
    throw error;
  } finally {
    flushing = false;
  }
}

function setLastRoute(route) {
  getStorage()?.setItem(LAST_ROUTE_KEY, JSON.stringify(route));
}

function getLastRoute() {
  return safeJsonParse(getStorage()?.getItem(LAST_ROUTE_KEY), {});
}

function setupWebLifecycle() {
  const win = getWindow();
  if (!win) return;
  win.addEventListener("online", () => {
    online = true;
    networkType = getWebNetworkType();
    scheduleFlush(200);
  });
  win.addEventListener("offline", () => {
    online = false;
    networkType = "offline";
  });
  win.addEventListener("pagehide", () => {
    flushMallEventLogQueue({ keepalive: true }).catch(() => undefined);
  });
  win.document?.addEventListener("visibilitychange", () => {
    if (win.document.visibilityState === "hidden") {
      flushMallEventLogQueue({ keepalive: true }).catch(() => undefined);
    }
  });
}

export function initializeMallEventLog() {

  if (initialized || !getSupportEventLog()) return;
  initialized = true;
  const win = getWindow();
  online = !win || win.navigator?.onLine !== false;
  networkType = getWebNetworkType();
  getDeviceId();
  getSessionId();
  getFirstTouch();
  setupWebLifecycle();
  if (readQueue().length) scheduleFlush(1200);
}

function sanitizeRequestUrl(url = "") {
  const raw = String(url || "").trim();
  if (!raw) return "";
  try {
    if (/^https?:\/\//i.test(raw)) {
      const parsed = new URL(raw);
      return `${parsed.pathname || ""}${parsed.search || ""}`.slice(0, 240);
    }
  } catch {}
  return raw.slice(0, 240);
}

function cleanupRequestFailureDedupMap() {
  const expireBefore = now() - REQUEST_FAILURE_DEDUP_WINDOW_MS;
  requestFailureDedupMap.forEach((value, key) => {
    if (value < expireBefore) requestFailureDedupMap.delete(key);
  });
}

export function reportMallApiRequestFailure(input = {}) {
  if (!getSupportEventLog(input.app)) return;
  initializeMallEventLog();
  const routeMeta = getLastRoute();
  const requestUrl = sanitizeRequestUrl(input.url);
  if (!requestUrl || REQUEST_FAILURE_IGNORE_PATTERNS.some(pattern => requestUrl.includes(pattern))) {
    return;
  }
  const message = String(input.message?.message || input.message || "").slice(0, 220);
  const requestMethod = (toNullableString(input.method) || "GET").toUpperCase();
  const status = toNullableNumber(input.status);
  const stage = toNullableString(input.stage) || "transport";
  const signature = [requestMethod, requestUrl, status ?? "na", stage, message || ""].join("::");
  const lastReportedAt = requestFailureDedupMap.get(signature) || 0;
  if (now() - lastReportedAt < REQUEST_FAILURE_DEDUP_WINDOW_MS) return;
  requestFailureDedupMap.set(signature, now());
  cleanupRequestFailureDedupMap();

  enqueue(buildPayload(API_REQUEST_FAIL_EVENT_CODE, {
    request_url: requestUrl,
    request_method: requestMethod,
    http_status: status ?? undefined,
    duration_ms: toNullableNumber(input.durationMs) ?? undefined,
    request_stage: stage,
    error_message: message,
    network_type: networkType,
    ...(input.ext || {}),
  }, {
    app: input.app,
    originalEventName: "mall_api_request_fail",
    screenName: input.screenName || routeMeta.screenName,
    routePath: input.routePath || routeMeta.routePath,
    fromRouteName: routeMeta.fromRouteName,
    fromRoutePath: routeMeta.fromRoutePath,
  }));
}

export function mallAnalyticsLogEvent(eventName, params = {}, app, googleAnalyticsParams = {}) {
  const eventCode = EVENT_CODE_MAP[eventName];
  if (!eventCode) return false;
  const googleAnalyticsTracked = trackGoogleAnalyticsEvent(eventName, {
    ...(params || {}),
    ...(googleAnalyticsParams || {}),
  });
  const pixelTracked = trackPixelAnalyticsEvent(eventName, params || {});
  if (!getSupportEventLog(app)) return googleAnalyticsTracked || pixelTracked;
  initializeMallEventLog();
  // console.log('mallAnalyticsLogEvent', eventName, params)
  const routeMeta = getLastRoute();
  enqueue(buildPayload(eventCode, params || {}, {
    app,
    originalEventName: eventName,
    screenName: pickValue(params || {}, ["screenName", "screen_name"]) || routeMeta.screenName,
    routePath: routeMeta.routePath,
    fromRouteName: routeMeta.fromRouteName,
    fromRoutePath: routeMeta.fromRoutePath,
  }));
  return googleAnalyticsTracked || pixelTracked;
}

export function mallAnalyticsLogScreenView(screenName, routeParams = {}, fromRouteName, fromRoutePath, app) {
  if (!screenName) return;
  trackGoogleAnalyticsPageView(screenName, routeParams || {}, fromRouteName, fromRoutePath);
  trackPixelPageView(screenName, routeParams || {});
  if (!getSupportEventLog(app)) return;
  initializeMallEventLog();
  const routePath = String(routeParams?.fullPath || routeParams?.path || screenName || "");
  setLastRoute({
    screenName,
    routePath,
    fromRouteName,
    fromRoutePath,
    updatedAt: now(),
  });
  enqueue(buildPayload("page_view", routeParams || {}, {
    app,
    originalEventName: "screen_view",
    screenName,
    routePath,
    fromRouteName,
    fromRoutePath,
    pageCode: resolvePageCode(screenName, routeParams || {}),
  }));
}

export function createMallEventLogContextFromNuxtStore(store, currentServer = "") {
  const state = store?.$state || store || {};
  const storeInfoMap = state.storeInfoDataMap || {};
  const storeInfo = storeInfoMap[currentServer] || Object.values(storeInfoMap)[0] || getStoredStoreInformation();
  return {
    storeInfo,
    mallUserInfo: state.mallUserInfo,
    userInfo: state.userInfo,
    userId: state.mallUserInfo?.id || state.userInfo?.id,
    mallApiUrl: parseOtherJson(storeInfo).mallApiUrl,
  };
}
